import React, { useMemo, useState } from "react";

// ==============================================================================
// GUN SEARCH
// A jump-to box over the whole model list. The brand -> model dropdowns still
// work exactly as before; this only saves the customer from having to know which
// manufacturer bucket their pistol sits in, and from reading a 230-entry list to
// find that a 19X lives inside "Glock 19/23/32 (FITS: GEN 3-6, 19M, 19X ...)".
//
// Matching strips everything that is not a letter or digit from both sides
// before comparing, so punctuation and spacing in the model names stop getting
// in the way: "p10c" finds "CZ P-10 C", "mp20" finds "S&W M&P 2.0", "tlr7" finds
// "TLR-7 / 7A / 7X". The fitment notes baked into the names are searchable too,
// so "19x", "mos" and "tacops" all land somewhere useful.
// ==============================================================================
const MAX_RESULTS = 8;

// "CZ P-10 C" -> "czp10c"
const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const GunSearch = ({ guns, brands, onPick }) => {
  const [query, setQuery] = useState("");

  const brandName = useMemo(() => {
    const map = {};
    (brands || []).forEach((b) => { map[b.id] = b.name; });
    return map;
  }, [brands]);

  const results = useMemo(() => {
    const q = norm(query);
    if (q.length < 2) return [];
    return (guns || [])
      .filter((g) => norm(`${g.name} ${brandName[g.brand_id] || ""}`).includes(q))
      .slice(0, MAX_RESULTS);
  }, [query, guns, brandName]);

  const q = query.trim();
  const showNoMatch = norm(query).length >= 2 && results.length === 0;

  const pick = (gun) => {
    onPick(gun);
    setQuery("");
  };

  return (
    <div className="option-group" style={{ position: "relative" }}>
      <label className="option-label">Find Your Firearm</label>

      <input
        type="text"
        className="option-select"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
        placeholder="Search any model - try 19X, P365, TacOps..."
        aria-label="Search firearm models"
        style={{ width: "100%", boxSizing: "border-box" }}
      />

      {results.length > 0 && (
        <div style={{ marginTop: "8px", border: "1px solid #333", borderRadius: "4px", backgroundColor: "#111", overflow: "hidden" }}>
          {results.map((gun) => (
            <button
              key={gun.id}
              type="button"
              onClick={() => pick(gun)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "11px 14px", border: "none", borderBottom: "1px solid #222", backgroundColor: "transparent", color: "#eee", fontSize: "14px", cursor: "pointer", lineHeight: "1.35" }}
            >
              {gun.name}
              <span style={{ display: "block", fontSize: "12px", color: "#888", marginTop: "2px" }}>
                {brandName[gun.brand_id] || ""}
              </span>
            </button>
          ))}
        </div>
      )}

      {showNoMatch && (
        <div style={{ marginTop: "8px", padding: "12px 14px", border: "1px solid #333", borderRadius: "4px", backgroundColor: "#111", fontSize: "13px", color: "#888" }}>
          No match for &ldquo;{q}&rdquo;. Try a shorter term, or pick the manufacturer below.
        </div>
      )}
    </div>
  );
};

export default GunSearch;
